export interface ThumbnailZoomConfig {
  /** Avatar service scale param (100 = normal, 200 = 2x) */
  scale: number;
  /** Avatar service translateX param */
  translateX: number;
  /** Avatar service translateY param */
  translateY: number;
}

export interface AvatarCategoryDefinition {
  key: string;
  label: string;
  type: 'component' | 'colour';
  options: string[];
  defaultValue: string;
  /** Show this category only when the referenced key equals one of the given values. */
  visibleWhen?: { key: string; value: string | string[] };
  /** Hide this category when the referenced key equals the given value. */
  hiddenWhen?: { key: string; value: string | string[] };
  /** For colour categories: the component category key this colour belongs to. */
  colourFor?: string;
  /** Zoom config for building static cropped thumbnail URLs. */
  zoom?: ThumbnailZoomConfig;
}

export interface AvatarStyleDefinition {
  id: string;
  label: string;
  description: string;
  license: string;
  baseUrl: string;
  categories: AvatarCategoryDefinition[];
  probabilityToggles: Record<string, string>;
  defaults: Record<string, string>;
  /** Extra keys to strip with `key[]` in thumbnail URLs (not user-selectable). */
  stripInThumbnails?: string[];
}

/**
 * A grouped category: a component category with its attached colour sub-category (if any).
 */
export interface AvatarCategoryGroup {
  component: AvatarCategoryDefinition;
  colours: AvatarCategoryDefinition[];
}

/**
 * Groups visible categories so colour categories are attached to their
 * parent component category rather than shown as separate sections.
 */
export function groupCategories(
  categories: AvatarCategoryDefinition[],
  options: Record<string, string>,
): AvatarCategoryGroup[] {
  const colourMap = new Map<string, AvatarCategoryDefinition[]>();
  const standalone: AvatarCategoryDefinition[] = [];

  for (const cat of categories) {
    if (cat.type === 'colour' && cat.colourFor) {
      const existing = colourMap.get(cat.colourFor) ?? [];
      existing.push(cat);
      colourMap.set(cat.colourFor, existing);
    }
  }

  for (const cat of categories) {
    // Skip colour categories that are attached to a component
    if (cat.type === 'colour' && cat.colourFor) continue;

    // Skip categories that are not visible
    if (!isCategoryVisible(cat, options)) continue;

    // Standalone colours and component categories are their own group
    standalone.push(cat);
  }

  return standalone.map((cat) => {
    if (cat.type === 'colour') {
      return { component: cat, colours: [] };
    }
    const attachedColours = colourMap.get(cat.key) ?? [];
    const visibleColours = attachedColours.filter((c) =>
      isCategoryVisible(c, options),
    );
    return { component: cat, colours: visibleColours };
  });
}

function isCategoryVisible(
  cat: AvatarCategoryDefinition,
  options: Record<string, string>,
): boolean {
  if (cat.visibleWhen) {
    const val = options[cat.visibleWhen.key] ?? '';
    const allowed = cat.visibleWhen.value;
    if (Array.isArray(allowed) ? !allowed.includes(val) : val !== allowed)
      return false;
  }
  if (cat.hiddenWhen) {
    const val = options[cat.hiddenWhen.key] ?? '';
    const hidden = cat.hiddenWhen.value;
    if (Array.isArray(hidden) ? hidden.includes(val) : val === hidden)
      return false;
  }
  return true;
}

const backgroundColourOptions = [
  'b6e3f4',
  'c0aede',
  'd1d4f9',
  'ffd5dc',
  'ffdfbf',
  'f0f0f0',
  'a3d9a5',
  'f9c74f',
  'f48c06',
  '90dbf4',
  'fee440',
  'ffffff',
];

export const avatarStyles: Record<string, AvatarStyleDefinition> = {
  avataaars: {
    id: 'avataaars',
    label: 'Avataaars',
    description: 'Cartoon-style illustrated avatars',
    license: 'Free for personal and commercial use',
    baseUrl: 'https://api.dicebear.com/9.x/avataaars/svg',
    categories: [
      {
        key: 'backgroundColor',
        label: 'Background',
        type: 'colour',
        options: backgroundColourOptions,
        defaultValue: 'b6e3f4',
      },
      {
        key: 'top',
        label: 'Hair',
        type: 'component',
        options: [
          'bigHair',
          'bob',
          'bun',
          'curly',
          'curvy',
          'dreads',
          'frida',
          'fro',
          'froBand',
          'longButNotTooLong',
          'miaWallace',
          'shavedSides',
          'straight01',
          'straight02',
          'straightAndStrand',
          'theCaesar',
          'theCaesarAndSidePart',
          'shortCurly',
          'shortFlat',
          'shortRound',
          'shortWaved',
          'sides',
          'winterHat02',
          'winterHat03',
          'winterHat04',
          'hat',
          'hijab',
          'turban',
        ],
        defaultValue: 'shortFlat',
      },
      {
        key: 'hairColor',
        label: 'Hair Colour',
        type: 'colour',
        options: [
          '2c1b18',
          '4a312c',
          '724133',
          'a55728',
          'b58143',
          'd6b370',
          'e8e1e1',
          'ecdcbf',
          'c93305',
        ],
        defaultValue: '2c1b18',
        colourFor: 'top',
        hiddenWhen: {
          key: 'top',
          value: ['winterHat02', 'winterHat03', 'winterHat04'],
        },
      },
      {
        key: 'hatColor',
        label: 'Hat Colour',
        type: 'colour',
        options: [
          '262e33',
          '65c9ff',
          '5199e4',
          '25557c',
          'e6e6e6',
          '929598',
          '3c4f5c',
          'b1e2ff',
          'a7ffc4',
          'ffdeb5',
          'ffafb9',
          'ffffb1',
          'ff488e',
          'ff5c5c',
          'ffffff',
        ],
        defaultValue: '262e33',
        colourFor: 'top',
        visibleWhen: {
          key: 'top',
          value: ['winterHat01', 'winterHat02', 'winterHat03', 'winterHat04'],
        },
      },
      {
        key: 'accessories',
        label: 'Accessories',
        type: 'component',
        options: [
          'blank',
          'kurt',
          'prescription01',
          'prescription02',
          'round',
          'sunglasses',
          'wayfarers',
        ],
        defaultValue: 'blank',
        zoom: {
          scale: 180,
          translateX: 0,
          translateY: 25,
        },
      },
      {
        key: 'accessoriesColor',
        label: 'Accessories Colour',
        type: 'colour',
        options: [
          '262e33',
          '65c9ff',
          '5199e4',
          '25557c',
          'e6e6e6',
          '929598',
          '3c4f5c',
          'b1e2ff',
          'a7ffc4',
          'ffdeb5',
          'ffafb9',
          'ffffb1',
          'ff488e',
          'ff5c5c',
          'ffffff',
        ],
        defaultValue: '262e33',
        colourFor: 'accessories',
        hiddenWhen: { key: 'accessories', value: 'blank' },
      },
      {
        key: 'facialHair',
        label: 'Facial Hair',
        type: 'component',
        options: [
          'blank',
          'beardLight',
          'beardMajestic',
          'beardMedium',
          'moustacheFancy',
          'moustacheMagnum',
        ],
        defaultValue: 'blank',
        zoom: {
          scale: 180,
          translateX: 0,
          translateY: -10,
        },
      },
      {
        key: 'facialHairColor',
        label: 'Facial Hair Colour',
        type: 'colour',
        options: ['2c1b18', '4a312c', '724133', 'a55728', 'b58143', 'd6b370'],
        defaultValue: '2c1b18',
        hiddenWhen: { key: 'facialHair', value: 'blank' },
        colourFor: 'facialHair',
      },
      {
        key: 'clothing',
        label: 'Clothing',
        type: 'component',
        options: [
          'blazerAndShirt',
          'blazerAndSweater',
          'collarAndSweater',
          'graphicShirt',
          'hoodie',
          'overall',
          'shirtCrewNeck',
          'shirtScoopNeck',
          'shirtVNeck',
        ],
        defaultValue: 'shirtCrewNeck',
      },
      {
        key: 'clothingGraphic',
        label: 'Clothing Graphic',
        type: 'component',
        options: [
          'bat',
          'bear',
          'cumbia',
          'deer',
          'diamond',
          'hola',
          'pizza',
          'resist',
          'skull',
          'skullOutline',
        ],
        defaultValue: 'bear',
        visibleWhen: { key: 'clothing', value: 'graphicShirt' },
      },
      {
        key: 'clothesColor',
        label: 'Clothes Colour',
        type: 'colour',
        options: [
          '262e33',
          '65c9ff',
          '5199e4',
          '25557c',
          'e6e6e6',
          '929598',
          'a7ffc4',
          'ffdeb5',
          'ffafb9',
          'ff488e',
          'ff5c5c',
          'ffffff',
        ],
        defaultValue: '65c9ff',
        colourFor: 'clothing',
      },
      {
        key: 'eyes',
        label: 'Eyes',
        type: 'component',
        options: [
          'default',
          'cry',
          'eyeRoll',
          'happy',
          'hearts',
          'side',
          'squint',
          'surprised',
          'wink',
          'winkWacky',
        ],
        defaultValue: 'default',
        zoom: {
          scale: 200,
          translateX: 0,
          translateY: 20,
        },
      },
      {
        key: 'eyebrows',
        label: 'Eyebrows',
        type: 'component',
        options: [
          'default',
          'defaultNatural',
          'flatNatural',
          'frownNatural',
          'raisedExcited',
          'raisedExcitedNatural',
          'sadConcerned',
          'sadConcernedNatural',
          'unibrowNatural',
          'upDown',
          'upDownNatural',
          'angry',
          'angryNatural',
        ],
        defaultValue: 'default',
        zoom: {
          scale: 200,
          translateX: 0,
          translateY: 32,
        },
      },
      {
        key: 'mouth',
        label: 'Mouth',
        type: 'component',
        options: [
          'default',
          'concerned',
          'disbelief',
          'eating',
          'grimace',
          'sad',
          'screamOpen',
          'serious',
          'smile',
          'tongue',
          'twinkle',
          'vomit',
        ],
        defaultValue: 'smile',
        zoom: {
          scale: 200,
          translateX: 0,
          translateY: -10,
        },
      },
      {
        key: 'skinColor',
        label: 'Skin Colour',
        type: 'colour',
        options: ['ffdbb4', 'edb98a', 'd08b5b', 'ae5d29', '614335', 'f8d25c'],
        defaultValue: 'edb98a',
      },
    ],
    probabilityToggles: {
      facialHair: 'facialHairProbability',
      accessories: 'accessoriesProbability',
    },
    defaults: {
      backgroundColor: 'b6e3f4',
      top: 'shortFlat',
      hairColor: '2c1b18',
      hatColor: '262e33',
      accessories: 'blank',
      accessoriesColor: '262e33',
      facialHair: 'blank',
      facialHairColor: '2c1b18',
      clothing: 'shirtCrewNeck',
      clothesColor: '65c9ff',
      clothingGraphic: 'bear',
      eyes: 'default',
      eyebrows: 'default',
      mouth: 'smile',
      skinColor: 'edb98a',
    },
    stripInThumbnails: ['nose'],
  },
  openPeeps: {
    id: 'openPeeps',
    label: 'Open Peeps',
    description: 'Hand-drawn illustration style',
    license: 'CC0 1.0 Universal',
    baseUrl: 'https://api.dicebear.com/9.x/open-peeps/svg',
    categories: [
      {
        key: 'backgroundColor',
        label: 'Background',
        type: 'colour',
        options: backgroundColourOptions,
        defaultValue: 'c0aede',
      },
      {
        key: 'head',
        label: 'Head',
        type: 'component',
        options: [
          'afro',
          'bangs',
          'bangs2',
          'bun',
          'bun2',
          'buns',
          'cornrows',
          'cornrows2',
          'dreads1',
          'dreads2',
          'flatTop',
          'flatTopLong',
          'grayBun',
          'grayMedium',
          'grayShort',
          'hatBeanie',
          'hatHip',
          'long',
          'longAfro',
          'longBangs',
          'longCurly',
          'medium1',
          'medium2',
          'medium3',
          'mediumBangs',
          'mediumBangs2',
          'mediumBangs3',
          'mediumStraight',
          'mohawk',
          'mohawk2',
          'noHair1',
          'noHair2',
          'noHair3',
          'popieSide',
          'short1',
          'short2',
          'short3',
          'short4',
          'short5',
          'twists',
          'twists2',
        ],
        defaultValue: 'short1',
      },
      {
        key: 'face',
        label: 'Face',
        type: 'component',
        options: [
          'angryWithFang',
          'awe',
          'blank',
          'calm',
          'cheeky',
          'concerned',
          'concernedFear',
          'contempt',
          'cute',
          'cyclops',
          'driven',
          'eatingHappy',
          'explaining',
          'eyesClosed',
          'fear',
          'hepiMouth',
          'lovingGrin1',
          'lovingGrin2',
          'monster',
          'old',
          'rage',
          'serious',
          'smile',
          'smileBig',
          'smileLOL',
          'smileTeethGap',
          'solemn',
          'suspicious',
          'tired',
        ],
        defaultValue: 'smile',
        zoom: {
          scale: 200,
          translateX: 0,
          translateY: 5,
        },
      },
      {
        key: 'accessory',
        label: 'Accessories',
        type: 'component',
        options: [
          'none',
          'glasses',
          'glasses2',
          'glasses3',
          'glasses4',
          'glasses5',
          'sunglasses',
          'sunglasses2',
        ],
        defaultValue: 'none',
        zoom: {
          scale: 180,
          translateX: 0,
          translateY: 5,
        },
      },
      {
        key: 'facialHair',
        label: 'Facial Hair',
        type: 'component',
        options: [
          'none',
          'chin',
          'full',
          'full2',
          'full3',
          'full4',
          'goatee1',
          'goatee2',
          'moustache1',
          'moustache2',
          'moustache3',
          'moustache4',
          'moustache5',
          'moustache6',
          'moustache7',
          'moustache8',
          'moustache9',
        ],
        defaultValue: 'none',
        zoom: {
          scale: 180,
          translateX: 0,
          translateY: 10,
        },
      },
      {
        key: 'body',
        label: 'Body',
        type: 'component',
        options: [
          'checkered',
          'device',
          'blazer',
          'buttonUp',
          'coffee',
          'explaining',
          'gaming',
          'polka',
          'pointing',
          'relaxing',
          'shirt',
          'striped',
          'sweater',
          'thunder',
          'turtleneck',
          'wave',
        ],
        defaultValue: 'shirt',
      },
      {
        key: 'skinColor',
        label: 'Skin Colour',
        type: 'colour',
        options: ['ffdbb4', 'edb98a', 'd08b5b', 'ae5d29', '614335'],
        defaultValue: 'edb98a',
      },
    ],
    probabilityToggles: {
      facialHair: 'facialHairProbability',
      accessory: 'accessoriesProbability',
    },
    defaults: {
      backgroundColor: 'c0aede',
      head: 'short1',
      face: 'smile',
      accessory: 'none',
      facialHair: 'none',
      body: 'shirt',
      skinColor: 'edb98a',
    },
  },
};

export const avatarStyleList = Object.values(avatarStyles);
